import React from 'react';
import {
  Chatbot,
  IngestWithContext,
  Configuration,
  DocumentDetailPageWithContext,
  IngestTable,
} from '@wealthlane/chatbot';
import { axiosInstance } from '../../services/apiInterceptor';
import { useGetCurrentTenantInfo, useWealthlaneCustomerApi } from '../../hooks';
import { toast } from 'react-toastify';
import Heading from '../typography';
import { PageHeader, WrapperContainer } from '../../constants/style/common.styled';
import { ProfileContainer } from '../../pages/settings/company-profile/profile.styled';
import ToastComponent from '../toast';
import { useNavigate } from 'react-router-dom';

// Define TChatConfig type locally since it's not exported from @wealthlane/chatbot v0.1.7
type TChatConfig = {
  responseProtocol: string;
  responseType: string;
  endpoint?: string;
  timeout?: number;
  ingestionEndpoint?: string;
  disclaimer?: string;
};

export const configState: TChatConfig = {
  responseProtocol: 'websocket' as const as string,
  responseType: 'stream' as const as string,
  endpoint: process.env.REACT_APP_CHATBOT_WEBSOCKET_API,
  timeout: 30000,
  ingestionEndpoint: process.env.REACT_APP_INGESTION_ENDPOINT,
  disclaimer: 'For assistance, please contact your dedicated specialist.',
};

function IngestComponent() {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: currentTenantInfo } = useGetCurrentTenantInfo(handleRequest);
  const navigate = useNavigate();

  const sendMessage = (message: string) => {
    toast.success(message);
  };

  return (
    <>
      <PageHeader>
        <Heading variant='h3' title='AI Knowledge Base Management' id='ingest' />
        <Heading variant='body-s' title='Control how the user interacts with your AI' id='ingest' />
      </PageHeader>

      <IngestWithContext
        axiosInstance={axiosInstance}
        config={configState}
        tenantIdAccessor={() => currentTenantInfo?.id}
        alertFunctionSuccess={(message: string) => {
          toast.success(<ToastComponent label='Success' message={message} />);
        }}
        alertFunctionError={(message: string) => {
          toast.error(<ToastComponent label='Error' message={message} />);
        }}
        metadataDocumentLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/ingest/document/metadata/list?status=uploaded`}
        createDocumentLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/generate-presigned-url`}
        previewDocumentLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/get-preview-presigned-url`}
        saveDocumentLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/ingest/publish`}
        documentListLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/ingest/document/list`}
        setAsActiveDocumentLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/ingest/document/version/rollback`}
      />

      <IngestTable
        axiosInstance={axiosInstance}
        config={configState}
        alertFunctionSuccess={(message: string) => {
          toast.success(<ToastComponent label='Success' message={message} />);
        }}
        alertFunctionError={(message: string) => {
          toast.error(<ToastComponent label='Error' message={message} />);
        }}
        tenantIdAccessor={() => currentTenantInfo?.id}
        versionInfoLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/ingest/document/version/list?logical_id=`}
        documentListLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/ingest/document/list`}
        setAsActiveDocumentLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/ingest/document/version/rollback`}
        previewDocumentLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/get-preview-presigned-url`}
        promoteDocumentLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/ingest/document/update-status`}
        allowPromote={true}
        allowTest={true}
      />
    </>
  );
}

function DocumentDetailComponent() {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: currentTenantInfo } = useGetCurrentTenantInfo(handleRequest);
  const navigate = useNavigate();

  return (
    <WrapperContainer data-page='documentDetail' id='pageContainer'>
      <DocumentDetailPageWithContext
        axiosInstance={axiosInstance}
        config={configState}
        tenantIdAccessor={() => currentTenantInfo?.id}
        alertFunctionSuccess={(message: string) => alert(message)}
        alertFunctionError={(message: string) => alert(message)}
        chunkPreviewLink={`${process.env.REACT_APP_CHATBOT_REST_URL}/ingest/document/chunks`}
        goBackFn={() => navigate("/")}
        navigate={navigate}
      />
    </WrapperContainer>
  );
}

export const Ingest = IngestComponent;
export const DocumentDetail = DocumentDetailComponent;
